import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, Event as EventIcon } from '@mui/icons-material';
import { isValid } from 'date-fns';
import {
  DialogZoomTransition,
  DIALOG_ZOOM_MS,
  dialogZoomSlotProps,
  dialogZoomDialogSx,
  dialogZoomPaperSx,
} from '../../../ui/DialogZoomTransition';
import CommercialDateTimePicker from '../../shared/CommercialDateTimePicker/CommercialDateTimePicker';
import { colors } from '../../../../styles/theme';
import { CancelButton, SubmitButton } from '../ReturnOrderModal/ReturnOrderModal.styles';
import { getCommercialSaleDate } from '../../../../utils/sales/commercial-sale-date';

export interface EditSaleDateModalProps {
  open: boolean;
  onClose: () => void;
  orderCode?: string;
  saleDate?: string | Date | null;
  createdAt: string | Date;
  loading?: boolean;
  error?: string | null;
  onSubmit: (saleDateIso: string) => void | Promise<void>;
}

const EditSaleDateModal: React.FC<EditSaleDateModalProps> = ({
  open,
  onClose,
  orderCode,
  saleDate,
  createdAt,
  loading = false,
  error = null,
  onSubmit,
}) => {
  const [value, setValue] = useState<Date>(() => getCommercialSaleDate({ saleDate, createdAt }));

  useEffect(() => {
    if (open) {
      setValue(getCommercialSaleDate({ saleDate, createdAt }));
    }
  }, [open, saleDate, createdAt]);

  const canSave = isValid(value) && !loading;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    void onSubmit(value.toISOString());
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      fullWidth
      TransitionComponent={DialogZoomTransition}
      transitionDuration={DIALOG_ZOOM_MS}
      slotProps={dialogZoomSlotProps}
      sx={dialogZoomDialogSx}
      PaperProps={{
        sx: (theme) =>
          dialogZoomPaperSx({
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 20px 60px rgba(0, 0, 0, 0.55)'
                : '0 20px 60px rgba(2, 6, 23, 0.18)',
            maxHeight: '92vh',
            width: '100%',
            maxWidth: { xs: 'calc(100% - 24px)', sm: 480 },
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }),
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
      >
        <DialogTitle
          sx={{
            padding: '16px 24px',
            borderBottom: 1,
            borderColor: 'divider',
            background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 600,
            m: 0,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{ minWidth: 0, flex: 1, pr: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}
          >
            <EventIcon sx={{ fontSize: 22, opacity: 0.95 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: '#ffffff',
                  fontSize: '1.15rem',
                  lineHeight: 1.3,
                }}
              >
                Editar fecha y hora de la venta
              </Typography>
              {orderCode ? (
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    color: '#ffffff',
                    opacity: 0.95,
                    wordBreak: 'break-word',
                  }}
                >
                  {orderCode}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={loading}
            sx={{
              color: '#ffffff',
              flexShrink: 0,
              '&:hover': { color: colors.grey[200] },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={(theme) => ({
            pt: 2.5,
            pb: 2,
            px: 3,
            overflowY: 'auto',
            flex: 1,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.default
                : theme.palette.grey[50],
          })}
        >
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
            El cambio se registra en el historial (usuario y sucursal).
          </Typography>
          <CommercialDateTimePicker
            label="Fecha y hora de la venta"
            value={value}
            onChange={setValue}
            disabled={loading}
          />
        </DialogContent>

        <DialogActions
          sx={(theme) => ({
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: theme.palette.background.paper,
            gap: 1,
          })}
        >
          <CancelButton onClick={onClose} disabled={loading}>
            Cancelar
          </CancelButton>
          <SubmitButton type="submit" disabled={!canSave}>
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Guardar'}
          </SubmitButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditSaleDateModal;
